import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect } from "react";
import AddBottomButton from "../../../../components/Add/AddBottomButton";
import AddModel from "../../../../components/Add/AddModel";
import Breadcrumbs from "../../../../components/breadcrumbs/Breadcrumbs";
import AddInputs from "../../../../components/Add/AddInputs";
import {
  setCVMDates,
  addCVMTarget,
  updateCVMTarget,
  removeCVMTarget,
  setCVMErrors,
  clearCVMErrors
} from "../addMDSlice";
import Image from "../../../../components/images/Image";
import useApi from "../../../../services/useApi";
import { showToast } from "../../../../components/toastMessage/toastSlice";
import TermsAndConditionsModal from "../../../../components/CustomModal/TermsAndConditionsModal";
import { RootState } from "../../../../store";
import { clearSignaturePath } from "../../../../store/slices/signatureSlice";

interface CVMTarget {
  target_amount: number | string;
  on_target_achievement_percentage: number | string;
  id?: string;
}

interface CVMDetails {
  from_date: string;
  to_date: string;
  targets: CVMTarget[];
}

interface ErrorState {
  [key: string]: string;
}

export default function AddCVM({
  id,
  type,
  setAddCvmClicked,
  setTermsAndConditions,
  termsAndConditions,
  edit
}: {
  id?: string;
  type: string;
  setAddCvmClicked?: (value: boolean) => void;
  setTermsAndConditions?: (value: boolean) => void;
  termsAndConditions?: boolean;
  edit?: boolean;
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { PatchAPI } = useApi();
  // const { id } = useParams();

  // Get initial values from Redux
  const reduxCVMData = useSelector((state: RootState) => state.addMD.cvm);
  const { user_id, to_date } = useSelector((state: RootState) => state.addMD);

  // Initialize local state with Redux data
  const [formData, setFormData] = useState<CVMDetails>({
    from_date: reduxCVMData.from_date || "",
    to_date: reduxCVMData.to_date || "",
    targets: reduxCVMData.targets.length > 0
      ? [...reduxCVMData.targets]
      : [{
        target_amount: '',
        on_target_achievement_percentage: ''
      }]
  });
  const [isExpired, setIsExpired] = useState(false);
  const [localErrors, setLocalErrors] = useState<ErrorState>({});
  const [acceptButtonLoading, setAcceptButtonLoading] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [ButtonLoading, setButtonLoading] = useState(false);

  // Date inputs configuration
  const dateInputs = {
    "": {
      "From Date": {
        required: true,
        id: "from_date",
        type: "date",
        tabIndex: 0,
        disabled: false,
        note: edit && to_date && new Date(to_date?.replace(/-/g, ', ') || '') < new Date() ? `Note: The current CVM expired on ${new Date(to_date || '').toLocaleDateString()}. Kindly set a new date range to proceed.` : undefined
      },
      "To Date": {
        required: true,
        id: "to_date",
        type: "date",
        tabIndex: 1,
        disabled: false
      },
    },
  };
  // Target inputs configuration for each target
  const targetInputs = {
    "": {
      "Target Amount": {
        required: true,
        id: "target_amount",
        type: "currency",
        tabIndex: 0,
        disabled: false
      },
      "On Target Achievement Percentage": {
        required: true,
        id: "on_target_achievement_percentage",
        type: "numberInput",
        tabIndex: 1,
        percentage: true,
        min: "0",
        max: "100",
        step: "0.01",
        disabled: false
      },
    },
  };

  // Handle date input changes
  const handleDateChange = (id: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));

    // Clear error for the changed field
    setLocalErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[id];
      return newErrors;
    });
  };

  // Handle target input changes
  const handleTargetChange = (index: number, id: string, value: string) => {
    const field = id === "target_amount" ? "target_amount" : "on_target_achievement_percentage";

    setFormData(prev => {
      const newTargets = [...prev.targets];
      if (value === "" || value.endsWith(".") || value.endsWith("0") || value.endsWith(".00") || value === "0" || value === "00") {
        newTargets[index] = { ...newTargets[index], [field]: value };
      } else {
        const numValue = parseFloat(parseFloat(value).toFixed(2));
        if (!isNaN(numValue)) {
          newTargets[index] = { ...newTargets[index], [field]: numValue };
        }
      }
      return { ...prev, targets: newTargets };
    });

    // Clear errors for the changed target field
    setLocalErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`targets[${index}].${field}`];
      return newErrors;
    });
  };  // Add a new target
  const handleAddTarget = () => {
    setFormData(prev => ({
      ...prev,
      targets: [...prev.targets, { target_amount: '', on_target_achievement_percentage: '' }]
    }));
  };

  // Remove a target
  const handleRemoveTarget = (index: number) => {
    setFormData(prev => ({
      ...prev,
      targets: prev.targets.filter((_, i) => i !== index)
    }));

    // Clear errors for the removed target
    setLocalErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`targets[${index}].target_amount`];
      delete newErrors[`targets[${index}].on_target_achievement_percentage`];
      return newErrors;
    });
  };

  // Handle form submission
  const handleSubmit = async () => {
    const errors: ErrorState = {};

    // Validation logic
    if (!formData.from_date) {
      errors["from_date"] = "This field is required";
    }

    if (!formData.to_date) {
      errors["to_date"] = "This field is required";
    }

    // // New validation: to_date must be a future date
    if (formData.to_date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Ignore time part for comparison
      const toDate = new Date(formData.to_date);
      if (toDate <= today) {
        errors["to_date"] = "Please select a future date for the 'To Date'";
      }
    }

    // Check if edit is true and validate from_date against Redux to_date
    if (edit && formData.from_date && to_date) {
      if (new Date(formData.from_date) <= new Date(to_date)) {
        errors["from_date"] = `'From Date' must be after the previous 'To Date'`;
        // setIsExpired
        // Remove the note from from_date input by updating dateInputs
        setFormData(prev => ({ ...prev })); // Trigger re-render to remove note
      }
    }

    if (
      formData.from_date &&
      formData.to_date &&
      new Date(formData.from_date) > new Date(formData.to_date)
    ) {
      errors["to_date"] = "'To Date' should be greater than the 'From Date'.";
    }

    formData.targets.forEach((target, index) => {
      if (target.target_amount === '') {
        errors[`targets[${index}].target_amount`] = "This field is required";
      }
      if (Number(target.target_amount) < 1 && target.target_amount !== "") {
        errors[`targets[${index}].target_amount`] =
          "Amount must be greater than or equal to 1";
      }


      if (Number(target.on_target_achievement_percentage) < 1) {
        errors[`targets[${index}].on_target_achievement_percentage`] =
          "Percentage should be greater than or equal to 1";
      }

      if (Number(target.on_target_achievement_percentage) === 0) {
        errors[`targets[${index}].on_target_achievement_percentage`] =
          "This field is required";
      }

      if (Number(target.on_target_achievement_percentage) > 100) {
        errors[`targets[${index}].on_target_achievement_percentage`] =
          "Achievement percentage cannot exceed 100%";
      }
    });

    // Check for duplicate targets
    const targetAmounts = formData.targets.map((t) => Number(parseFloat(String(t.target_amount)).toFixed(2)));
    const duplicateIndices: number[] = [];
    targetAmounts.forEach((amount, i) => {
      if (!isNaN(amount) && targetAmounts.findIndex((a) => a === amount) !== i) {
        duplicateIndices.push(i);
      }
    });
    duplicateIndices.forEach((idx) => {
      errors[`targets[${idx}].target_amount`] =
        "Target already defined. Please select a new one.";
    });
    if (Object.keys(errors).length > 0) {
      setLocalErrors(errors);
      return;
    }

    setButtonLoading(true);

    // Clean up decimal points in target values before submitting
    const cleanedFormData = {
      ...formData,
      targets: formData.targets.map(target => {
        const { id, ...cleanTarget } = { ...target };

        // Handle target_amount with trailing decimal point
        if (typeof cleanTarget.target_amount === 'string' && cleanTarget.target_amount.endsWith('.')) {
          cleanTarget.target_amount = parseFloat(cleanTarget.target_amount);
        }

        if (cleanTarget.target_amount) {
          cleanTarget.target_amount = Number(cleanTarget.target_amount);
        }
        // Handle on_target_achievement_percentage with trailing decimal point
        if (typeof cleanTarget.on_target_achievement_percentage === 'string' &&
          cleanTarget.on_target_achievement_percentage.endsWith('.')) {
          cleanTarget.on_target_achievement_percentage = parseFloat(cleanTarget.on_target_achievement_percentage);
        }
        // Always convert on_target_achievement_percentage to number
        if (cleanTarget.on_target_achievement_percentage) {
          cleanTarget.on_target_achievement_percentage = Number(cleanTarget.on_target_achievement_percentage);
        }

        return cleanTarget;
      })
    };
    // Dispatch to Redux before API call
    dispatch(setCVMDates(cleanedFormData));

    let payload = {
      cvm: cleanedFormData,
    };

    let res = await PatchAPI(
      `${type?.toLowerCase()?.split(" ").join("-")}/${user_id}`,
      payload
    );

    if (!res.error) {
      dispatch(setCVMErrors({}));
      dispatch(showToast({ message: "CVM updated successfully", type: "success" }));
      setTermsAndConditions?.(true);
      if (edit) {
        navigate(-1)
      }
      setButtonLoading(false);
    } else {
      dispatch(showToast({ message: res?.data?.response?.data?.message, type: "error" }));
      setButtonLoading(false);
    }
  };

  const handleTermsAndConditions = async (signature: string) => {
    setTermsAndConditions?.(true);
    setAcceptButtonLoading(true);
    let payload = {
      signature_photo: signature,
    };

    let res = await PatchAPI(
      `${type?.toLowerCase()?.split(" ").join("-")}/${user_id}`,
      payload
    );
    if (!res.error) {
      setTermsAndConditions?.(false)
      navigate(`/user-management/${type
        ?.toLowerCase()
        .replace(/ /g, "-")
        .slice(0, -1)}-management`);
      dispatch(showToast({ message: "Details added successfully", type: "success" }));
      dispatch(clearSignaturePath());
    } else {
      dispatch(showToast({ message: res?.data?.response?.data?.message, type: "error" }));
    }
    setAcceptButtonLoading(false);
  };

  // Check form validity
  useEffect(() => {
    const isValid =
      formData.from_date !== "" &&
      formData.to_date !== "" &&
      formData.targets.length > 0 &&
      formData.targets.every(
        (target) =>
          Number(target.target_amount) > 0 &&
          Number(target.on_target_achievement_percentage) > 0 &&
          Number(target.on_target_achievement_percentage) <= 100
      );

    setIsFormValid(isValid);
  }, [formData]);

  useEffect(() => {
    setIsExpired(new Date(reduxCVMData.to_date) > new Date());
  }, [])

  return (
    <div>
      <Breadcrumbs
        navPath={[
          "User Management",
          `${type.slice(0, -1)} Management`,
          ...(edit ? (
            new Date(reduxCVMData.to_date) > new Date() ? 
              [`List of ${type}`, `View ${type.slice(0, -1)} Profile`, "Edit CVM"] :
              reduxCVMData?.to_date ? 
                [`List of ${type}`, `View ${type.slice(0, -1)} Profile`, "Edit CVM"] :
                [`List of ${type}`, `View ${type.slice(0, -1)} Profile`, "Add CVM"]
          ) : ["Add CVM"]),
        ]}
        links={[
          `/user-management/${type
            ?.toLowerCase()
            .replace(/ /g, "-")
            .slice(0, -1)}-management`,
          `/user-management/${type
            ?.toLowerCase()
            .replace(/ /g, "-")
            .slice(0, -1)}-management`,
          ...(edit ? [
            `/user-management/${type
              ?.toLowerCase()
              .replace(/ /g, "-")
              .slice(0, -1)}-management`,
            `/user-management/${type
              ?.toLowerCase()
              .replace(/ /g, "-")
              .slice(0, -1)}-management/view-${type
                ?.toLowerCase()
                .replace(/ /g, "-")
                .slice(0, -1)}/${user_id}`
          ] : []),
        ]}
        heading={edit && (new Date(reduxCVMData.to_date) > new Date() || reduxCVMData?.to_date) ? 'Edit CVM' : `Add CVM`}
      />
      <AddModel>
        {/* Date inputs */}
        <AddInputs
          mapping={true}
          index={2}
          errors={localErrors}
          totalInputCount={2}
          onInputChange={handleDateChange}
          inputs={dateInputs}
          inputValues={{
            from_date: formData.from_date,
            to_date: formData.to_date,
          }}
        />
        {/* {edit && new Date(reduxCVMData.to_date) > new Date() && (
          <p className="text-sm text-red-600 mt-1">Previous CVM has expired. You can set a new date range.</p>
        )} */}

        {/* CVM Targets Section */}
        <div className="mt-6">
          {formData.targets.map((target, index) => {
            return (
              <div key={index} className="bg-blue-50 flex p-4 mb-4 rounded-md">
                <AddInputs
                  mapping={true}
                  customWidth={true}
                  index={index}
                  errors={{
                    target_amount:
                      localErrors[`targets[${index}].target_amount`],
                    on_target_achievement_percentage:
                      localErrors[
                      `targets[${index}].on_target_achievement_percentage`
                      ],
                  }}
                  totalInputCount={2}
                  onInputChange={(id, value) =>
                    handleTargetChange(index, id, value)
                  }
                  inputs={targetInputs}
                  inputValues={{
                    target_amount: target.target_amount,
                    on_target_achievement_percentage:
                      target.on_target_achievement_percentage,
                  }}
                />
                <div className="flex items-center">
                  {formData.targets.length > 1 && (
                    <Image
                      src={"delete_blue"}
                      className="cursor-pointer"
                      onClick={() => handleRemoveTarget(index)}
                    />
                  )}
                </div>
              </div>
            );
          })}
          {formData.targets.length < 5 && (
            <button
              type="button"
              className="text-p_primary mt-2 font-medium flex items-center gap-1"
              data-testid="add_service_detail"
              onClick={handleAddTarget}
            >
              <span className="text-3xl">
                + 
              </span>
              Add (You can add up to {5 - formData.targets.length} CVM)
            </button>
          )}
        </div>
      </AddModel>

      <AddBottomButton
        buttonName="Save & Next"
        testID="save_cvm"
        buttonClass="w-[140px]"
        onClick={handleSubmit}
        buttonWidth="w-[140px]"
        loading={ButtonLoading}
        secondaryButtonName={edit ? '' : "Previous"}
        secondaryButtonClass="w-[120px] font-semibold text-small"
        secondaryButtonFunction={() => {
          dispatch(clearCVMErrors());
          setAddCvmClicked?.(false);
        }}
      />
      {termsAndConditions && <TermsAndConditionsModal
        isOpen={termsAndConditions}
        onClose={() => {
          setTermsAndConditions?.(false)
          dispatch(clearSignaturePath());
          // setIsModalOpen(true)
        }}
        buttonLoading={acceptButtonLoading}
        secondaryButtonText="Decline"
        primaryButtonText="Accept"
        maxWidth="max-w-[461px]"
        buttonWidth="w-[85px]"
        // primaryButtonClassName
        handleSubmitButtonClick={handleTermsAndConditions}
      />}
    </div>
  );
}